import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import MatchSearchLoader from "../common/MatchSearchLoader";
import { colors } from "../../theme/colors";
import {
    MOBILE_NAV_HEIGHT,
    MOBILE_NAV_MEDIA,
} from "../layout/layoutConstants";
import {
    playSmallerMatchOfferSoundOnce,
    clearSmallerMatchOfferSound,
} from "../../universal/matchNotificationSounds";
import { getSmallerMatchOptedInCopy } from "../lobby/smallerMatchIntro";

const MotionBox = motion(Box);

function formatElapsedSeconds(timestamp) {
    if (!timestamp) return "00:00";
    const nowUnix = Math.floor(Date.now() / 1000);
    const elapsed = Math.max(0, nowUnix - timestamp);
    const mm = Math.floor(elapsed / 60).toString().padStart(2, "0");
    const ss = (elapsed % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
}

export default function GlobalMatchmakingStatus({
    timestamp,
    queueStatus,
    user,
    actions,
}) {
    const [elapsedLabel, setElapsedLabel] = useState(() =>
        formatElapsedSeconds(timestamp)
    );
    const smallerMatchOffer = queueStatus?.smallerMatchOffer || null;
    const smallerMatchTeamSize = Number(smallerMatchOffer?.teamSize);
    const smallerMatchLabel = Number.isInteger(smallerMatchTeamSize)
        ? `${smallerMatchTeamSize}v${smallerMatchTeamSize}`
        : "";
    const smallerMatchStatus = smallerMatchOffer?.status || null;
    const showSmallerMatchOffer =
        Boolean(smallerMatchOffer) &&
        smallerMatchStatus !== "declined" &&
        Boolean(smallerMatchLabel);
    const smallerMatchOptedInCopy = smallerMatchStatus === "opted_in"
        ? getSmallerMatchOptedInCopy({
            teamLabel: smallerMatchLabel,
            optedInCount: smallerMatchOffer.optedInCount,
            matchSize: smallerMatchOffer.matchSize,
            compact: true,
        })
        : null;

    useEffect(() => {
        setElapsedLabel(formatElapsedSeconds(timestamp));
        const id = window.setInterval(() => {
            setElapsedLabel(formatElapsedSeconds(timestamp));
        }, 1000);
        return () => window.clearInterval(id);
    }, [timestamp]);

    useEffect(() => {
        if (showSmallerMatchOffer && smallerMatchStatus === "offered") {
            playSmallerMatchOfferSoundOnce(smallerMatchOffer);
            return undefined;
        }
        if (!smallerMatchOffer) {
            clearSmallerMatchOfferSound();
        }
        return undefined;
    }, [
        showSmallerMatchOffer,
        smallerMatchStatus,
        smallerMatchTeamSize,
        smallerMatchOffer,
    ]);

    const handleSmallerMatchOptIn = () => {
        if (user?.userId) {
            actions?.lobbySmallerMatchOptIn?.({ userId: user.userId });
        }
    };

    const handleSmallerMatchDecline = () => {
        if (user?.userId) {
            actions?.lobbySmallerMatchDecline?.({ userId: user.userId });
        }
    };

    return (
        <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            sx={{
                position: "fixed",
                right: { xs: "0.7rem", md: "1.75rem" },
                left: { xs: "0.7rem", md: "auto" },
                bottom: {
                    xs: `calc(${MOBILE_NAV_HEIGHT} + 0.55rem + env(safe-area-inset-bottom, 0px))`,
                    md: "1.75rem",
                },
                zIndex: 1390,
                pointerEvents: "auto",
            }}
        >
            <Box
                role="status"
                aria-label={`Searching for a match for ${elapsedLabel}`}
                sx={{
                    width: { xs: "100%", md: "23rem" },
                    boxSizing: "border-box",
                    px: { xs: "0.75rem", md: "1rem" },
                    py: { xs: "0.65rem", md: "0.85rem" },
                    borderRadius: { xs: "14px", md: "20px" },
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.65rem",
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(28,30,34,0.92) 0%,
                            rgba(16,18,22,0.96) 100%
                        )
                    `,
                    border: `1px solid rgba(232,163,61,0.22)`,
                    backdropFilter: "blur(18px)",
                    boxShadow: `
                        0 18px 48px rgba(0,0,0,0.55),
                        0 0 32px rgba(232,163,61,0.12)
                    `,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: "0.6rem", md: "0.75rem" },
                    }}
                >
                    <MatchSearchLoader size="compact" diameter={42} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                color: colors.text.primary,
                                fontSize: { xs: "0.78rem", md: "0.88rem" },
                                fontWeight: 800,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                lineHeight: 1.15,
                            }}
                        >
                            Searching
                        </Typography>
                        <Typography
                            sx={{
                                mt: "0.15rem",
                                color: alpha(colors.text.secondary, 0.82),
                                fontSize: { xs: "0.68rem", md: "0.76rem" },
                                fontWeight: 500,
                                lineHeight: 1.35,
                            }}
                        >
                            You can keep browsing. We will alert you here.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            px: { xs: "0.55rem", md: "0.7rem" },
                            py: { xs: "0.28rem", md: "0.4rem" },
                            borderRadius: "10px",
                            background: "rgba(232,163,61,0.12)",
                            border: "1px solid rgba(232,163,61,0.22)",
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.primary.main,
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: { xs: "0.86rem", md: "0.95rem" },
                                fontWeight: 800,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {elapsedLabel}
                        </Typography>
                    </Box>
                </Box>

                {showSmallerMatchOffer ? (
                    <Box
                        sx={{
                            pt: "0.65rem",
                            borderTop: `1px solid ${alpha(colors.primary.main, 0.16)}`,
                        }}
                    >
                        <Typography
                            sx={{
                                m: 0,
                                color: alpha(colors.primary.main, 0.95),
                                fontSize: { xs: "0.7rem", md: "0.76rem" },
                                fontWeight: 800,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                            }}
                        >
                            {smallerMatchStatus === "opted_in"
                                ? smallerMatchOptedInCopy.title
                                : `Play ${smallerMatchLabel} instead?`}
                        </Typography>
                        <Typography
                            sx={{
                                mt: "0.25rem",
                                color: alpha(colors.text.secondary, 0.78),
                                fontSize: { xs: "0.66rem", md: "0.72rem" },
                                lineHeight: 1.35,
                            }}
                        >
                            {smallerMatchStatus === "opted_in"
                                ? smallerMatchOptedInCopy.hint
                                : "Same skill matching with smaller equal teams."}
                        </Typography>
                        {smallerMatchStatus === "offered" ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: "0.5rem",
                                    mt: "0.6rem",
                                    flexWrap: "wrap",
                                }}
                            >
                                <Button
                                    type="button"
                                    onClick={handleSmallerMatchOptIn}
                                    sx={{
                                        px: "0.85rem",
                                        py: "0.42rem",
                                        borderRadius: "7px",
                                        background: alpha(colors.primary.main, 0.16),
                                        border: `1px solid ${alpha(colors.primary.main, 0.42)}`,
                                        color: colors.primary.main,
                                        fontSize: "0.64rem",
                                        fontWeight: 800,
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                        transition:
                                            "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                                        "&:hover": {
                                            background: alpha(colors.primary.main, 0.22),
                                            borderColor: alpha(colors.primary.main, 0.6),
                                        },
                                    }}
                                >
                                    Play {smallerMatchLabel}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleSmallerMatchDecline}
                                    sx={{
                                        px: "0.85rem",
                                        py: "0.42rem",
                                        borderRadius: "7px",
                                        background: "transparent",
                                        border: `1px solid ${alpha(colors.text.secondary, 0.24)}`,
                                        color: alpha(colors.text.secondary, 0.82),
                                        fontSize: "0.64rem",
                                        fontWeight: 800,
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                        transition:
                                            "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, color 0.15s ease-in-out",
                                        "&:hover": {
                                            background: alpha("#fff", 0.04),
                                            borderColor: alpha(colors.text.secondary, 0.4),
                                            color: colors.text.primary,
                                        },
                                    }}
                                >
                                    Keep 5v5
                                </Button>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
            </Box>
        </MotionBox>
    );
}

GlobalMatchmakingStatus.propTypes = {
    timestamp: PropTypes.number,
    queueStatus: PropTypes.shape({
        smallerMatchOffer: PropTypes.shape({
            teamSize: PropTypes.number,
            matchSize: PropTypes.number,
            optedInCount: PropTypes.number,
            status: PropTypes.oneOf(["offered", "opted_in", "declined"]),
        }),
    }),
    user: PropTypes.object,
    actions: PropTypes.object,
};
